import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Check, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Brief 27 oct LOT E.3 — « Je suis intéressé·e » : quand aucun créneau ne convient
   (ou qu'aucun n'est proposé), l'apprenant signale son intérêt ; l'expert est
   notifié et retrouve la liste dans Mes sessions → « Personnes intéressées ». */
const ExpertInterestButton = ({ expertId, variant = 'full' }) => {
  const { user } = useAuth();
  const [state, setState] = useState({ interested: false, count: 0, loaded: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !expertId || user.user_id === expertId) return;
    axios.get(`${API}/experts/${expertId}/interest`, { withCredentials: true })
      .then((r) => setState({ ...r.data, loaded: true })).catch(() => setState((s) => ({ ...s, loaded: true })));
  }, [expertId, user]);

  if (!user || user.user_id === expertId) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/experts/${expertId}/interest`, {}, { withCredentials: true });
      setState({ ...data, loaded: true });
      if (data.interested) toast.success("Intérêt envoyé — l'expert est prévenu et te proposera un créneau.");
      else toast('Intérêt retiré');
    } catch (e) {
      toast.error(e.response?.data?.detail || "L'action n'a pas pu aboutir");
    } finally {
      setBusy(false);
    }
  };

  const others = state.count - (state.interested ? 1 : 0);

  if (variant === 'inline') {
    return (
      <button type="button" onClick={toggle} disabled={busy} data-testid="expert-interest-inline-btn"
        className={`inline-flex items-center gap-1.5 text-xs font-medium transition-colors ${state.interested ? 'text-rose-600 dark:text-rose-400' : 'text-gray-500 dark:text-gray-400 hover:text-rose-600'}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : state.interested ? <Check className="h-3.5 w-3.5" /> : <Heart className="h-3.5 w-3.5" />}
        {state.interested ? 'Intérêt envoyé — tu seras prévenu·e' : 'Aucun créneau ne convient ? Je suis intéressé·e'}
      </button>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2 text-center" data-testid="expert-interest-block">
      <button type="button" onClick={toggle} disabled={busy} data-testid="expert-interest-btn"
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
          state.interested
            ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-700'
            : 'bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:opacity-90 shadow'
        }`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : state.interested ? <Check className="h-4 w-4" /> : <Heart className="h-4 w-4" />}
        {state.interested ? 'Intérêt envoyé' : 'Je suis intéressé·e'}
      </button>
      <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs">
        {state.interested
          ? "L'expert est prévenu·e et te proposera un créneau. Clique à nouveau pour retirer."
          : "Pas encore de créneau proposé : signale ton intérêt, l'expert sera notifié·e."}
        {others > 0 && <span className="block mt-0.5 text-gray-400">{others} autre{others > 1 ? 's' : ''} personne{others > 1 ? 's' : ''} intéressée{others > 1 ? 's' : ''}</span>}
      </p>
    </div>
  );
};

export default ExpertInterestButton;
